import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquareText } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import type {
  MapaAtasResponse,
  MapaAtasItem,
} from "../../../../types/database";

interface DesenvolvimentoDrawerData {
  itemCodigo: string;
  itemDescricao: string;
  desenvolvimentos: MapaAtasItem["desenvolvimentos"];
}

interface MapaAtasGridProps {
  data: MapaAtasResponse;
}

export function MapaAtasGrid({ data }: MapaAtasGridProps) {
  const { t } = useTranslation('common');
  const { atas, topicos, itensSemTopico } = data;
  const [drawerData, setDrawerData] = useState<DesenvolvimentoDrawerData | null>(null);

  const handleOpenDesenvolvimentos = (item: MapaAtasItem) => {
    setDrawerData({
      itemCodigo: item.codigo,
      itemDescricao: item.descricao,
      desenvolvimentos: item.desenvolvimentos,
    });
  };

  return (
    <div className="relative">
      {/* Scroll container */}
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <table className="w-full border-collapse text-sm" style={{ minWidth: `${520 + atas.length * 72}px` }}>
          <thead className="sticky top-0 z-30">
            <tr>
              {/* Sticky header - Código */}
              <th className="sticky left-0 z-40 w-[100px] min-w-[100px] bg-gray-100 border-b-2 border-r border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                {t('mapaAtas.code')}
              </th>
              {/* Sticky header - Descrição */}
              <th className="sticky left-[100px] z-40 w-[420px] min-w-[420px] bg-gray-100 border-b-2 border-r-2 border-gray-200 px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)]">
                {t('mapaAtas.description')}
              </th>
              {/* Headers das atas */}
              {atas.map((ata, index) => (
                <th
                  key={ata.numero}
                  className={`w-[72px] min-w-[72px] bg-gray-100 border-b-2 border-gray-200 px-2 py-2 text-center ${
                    index < atas.length - 1 ? 'border-r border-gray-200' : ''
                  }`}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[10px] font-medium text-gray-500 uppercase">{t('mapaAtas.meeting')}</span>
                    <span className="text-sm font-bold text-gray-900">{ata.numero}</span>
                    <span className="text-[10px] text-gray-400">{ata.data}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Tópicos */}
            {topicos.map((topico) => (
              <React.Fragment key={topico.id}>
                {/* Header do tópico - Sticky horizontal */}
                <tr>
                  <td
                    colSpan={2}
                    className="sticky left-0 z-30 bg-gradient-to-r from-blue-50 to-blue-50/80 px-4 py-2.5 border-b border-blue-100 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]"
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-100 text-blue-700 text-xs font-bold">
                        {topico.codigo}
                      </span>
                      <span className="font-semibold text-gray-900 text-sm">{topico.titulo}</span>
                    </div>
                  </td>
                  {/* Células vazias para manter a estrutura da tabela */}
                  {atas.map((ata) => (
                    <td
                      key={ata.numero}
                      className="bg-blue-50/50 border-b border-blue-100 w-[72px] min-w-[72px]"
                    />
                  ))}
                </tr>
                {/* Itens do tópico */}
                {topico.itens.map((item, itemIndex) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    atas={atas}
                    isLast={itemIndex === topico.itens.length - 1}
                    onOpenDesenvolvimentos={handleOpenDesenvolvimentos}
                  />
                ))}
              </React.Fragment>
            ))}

          </tbody>
        </table>
      </div>

      {/* Drawer de Desenvolvimentos */}
      <Sheet open={!!drawerData} onOpenChange={(open) => !open && setDrawerData(null)}>
        <SheetContent className="overflow-y-auto overflow-x-hidden">
          <SheetHeader className="pb-4 border-b border-border">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded">
                {drawerData?.itemCodigo}
              </span>
            </div>
            <SheetTitle className="text-base leading-snug break-words">
              {drawerData?.itemDescricao}
            </SheetTitle>
            <SheetDescription>
              {drawerData?.desenvolvimentos.length}{" "}
              {drawerData?.desenvolvimentos.length === 1 ? t('mapaAtas.development') : t('mapaAtas.developments')}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {drawerData?.desenvolvimentos.map((dev, idx) => (
              <div
                key={idx}
                className="relative pl-4 border-l-2 border-blue-200 py-2"
              >
                <div className="absolute -left-[9px] top-3 w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs font-semibold">
                    {t('mapaAtas.meeting_number', { number: dev.ata_numero })}
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap">
                  {dev.texto}
                </p>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

interface ItemRowProps {
  item: MapaAtasItem;
  atas: MapaAtasResponse["atas"];
  isLast?: boolean;
  onOpenDesenvolvimentos: (item: MapaAtasItem) => void;
}

function ItemRow({ item, atas, isLast, onOpenDesenvolvimentos }: ItemRowProps) {
  return (
    <tr className="group hover:bg-gray-50/80 transition-colors duration-150">
      {/* Código - Sticky */}
      <td className={`sticky left-0 z-20 w-[100px] min-w-[100px] bg-white group-hover:bg-gray-50 transition-colors duration-150 px-3 py-3 border-r border-gray-100 ${!isLast ? 'border-b border-b-gray-100' : ''}`}>
        <span className="font-mono text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded">
          {item.codigo}
        </span>
      </td>

      {/* Descrição - Sticky */}
      <td className={`sticky left-[100px] z-20 w-[420px] min-w-[420px] bg-white group-hover:bg-gray-50 transition-colors duration-150 px-4 py-3 border-r-2 border-gray-200 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)] ${!isLast ? 'border-b border-b-gray-100' : ''}`}>
        <div className="text-sm text-gray-800 leading-relaxed line-clamp-2">{item.descricao}</div>
        {item.desenvolvimentos.length > 0 && (
          <button
            onClick={() => onOpenDesenvolvimentos(item)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1.5 select-none transition-colors"
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">
              {item.desenvolvimentos.length}
            </span>
            <MessageSquareText className="w-3.5 h-3.5" />
            <span>Ver desenvolvimentos</span>
          </button>
        )}
      </td>

      {/* Colunas das atas - Scrollable */}
      {atas.map((ata, index) => (
        <td
          key={ata.numero}
          className={`w-[72px] min-w-[72px] bg-white group-hover:bg-gray-50 transition-colors duration-150 px-2 py-3 text-center ${
            index < atas.length - 1 ? 'border-r border-gray-50' : ''
          } ${!isLast ? 'border-b border-b-gray-100' : ''}`}
        >
          {item.presencas.includes(ata.numero) ? (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 ring-1 ring-blue-100">
              <span className="w-3 h-3 rounded-full bg-blue-500 shadow-sm" />
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50/50">
              <span className="w-2 h-2 rounded-full bg-gray-200" />
            </span>
          )}
        </td>
      ))}
    </tr>
  );
}
